import React from 'react';
import NumberFormat from 'react-number-format';
import {
  TextFormattedNumber,
} from 'components';
import {
  FormControl,
  Grid,
  MenuItem,
  TextField,
} from '@material-ui/core';
import { calculateSavings } from 'pages/quotes/helpers';


const discountTypeOptions = [{
  label: 'Percentage Off',
  value: 'percent'
}, {
  label: 'Dollar Off',
  value: 'fixed'
}];

const QuoteItemSavings = ({
  formik,
  item,
  index,
  selectedDataSource
}) => {
  const {
    sku,
    discountType,
    discount,
    fixedPrice,
    qty
  } = item;

  const {
    handleChange,
    handleBlur,
    touched,
    errors,
    setFieldValue,
    values
  } = formik;

  const {
    productAttributes,
    productDiscounts
  } = selectedDataSource;

  const isAlleSKU = sku?.includes('bdsku');
  const savings = +productAttributes?.savings || 0;

  const discountOptions = (productDiscounts || []).reduce((acc, option) => {
    const { price_type, price, sort_order } = option;

    acc[price_type] = [...(acc[price_type] || []), { price_type, price, sort_order }];
    acc[price_type].sort((a, b) => a.price - b.price);

    return acc;
  }, {});

  const handleDiscountTypeChange = (event) => {
    handleChange(event);
    const { value } = event.target;

    const currentValues = values.items[index];

    const updatedValues = {
      discountType: value,
      discount: '',
      savings: calculateSavings(value, 0, fixedPrice, savings, qty),
    };

    setFieldValue(`items[${index}]`, { ...currentValues, ...updatedValues });
  };

  const handleOptionTypeChange = (event) => {
    handleChange(event);
    const { value } = event.target;
    const currentValues = values.items[index];

    let discount = value;

    if (discountOptions?.[discountType]?.length) {
      const matchingOption = discountOptions[discountType].find((o) => o.price_type === discountType && o.price === value);
      discount = matchingOption?.price || 0;
    }

    const updatedValues = {
      discount,
      savings: calculateSavings(discountType, discount, fixedPrice, savings, qty),
    };

    setFieldValue(`items[${index}]`, { ...currentValues, ...updatedValues });
  };

  const labelStyle: React.CSSProperties = {
    maxWidth: 'calc(100% - 24px)',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
  };

  return (
    <Grid container item xs={12} lg={3} xl={3} spacing={1}>
      {savings ?
        <Grid item xs={12}>
          <FormControl variant="outlined" fullWidth>
            <NumberFormat
              customInput={TextField}
              variant="outlined"
              name="savings"
              label="Savings"
              disabled
              value={savings}
              prefix="$"
              fixedDecimalScale
              decimalScale={2}
              InputLabelProps={{ style: labelStyle }}
            />
          </FormControl>
        </Grid>
        :
        <>
          <Grid item xs={12} lg={6} zeroMinWidth>
            <FormControl variant="outlined" fullWidth>
              {Object.keys(discountOptions).length > 0 ?
                <TextField
                  select
                  name={`items[${index}].discountType`}
                  variant="outlined"
                  value={item.discountType}
                  onChange={handleDiscountTypeChange}
                  onBlur={handleBlur}
                  disabled={!sku}
                  fullWidth
                  label="Discount Type"
                  InputLabelProps={{ style: labelStyle }}
                  error={touched.items?.[index]?.discountType && !!errors.items?.[index]?.discountType}
                  helperText={touched.items?.[index]?.discountType && errors.items?.[index]?.discountType}
                >
                  <MenuItem value="">No discount</MenuItem>
                  {isAlleSKU ?
                    <MenuItem value="fixed">Dollar Off</MenuItem>
                    :
                    Object.keys(discountOptions).map((option) => (
                      <MenuItem value={option} key={option}>
                        {option == 'percent' ? 'Percentage Off' : 'Dollar Off'}
                      </MenuItem>
                    ))}
                </TextField>
                :
                <TextField
                  select
                  name={`items[${index}].discountType`}
                  variant="outlined"
                  value={item.discountType}
                  onChange={handleDiscountTypeChange}
                  onBlur={handleBlur}
                  disabled={!sku}
                  fullWidth
                  label="Discount Type"
                  InputLabelProps={{ style: labelStyle }}
                  error={touched.items?.[index]?.discountType && !!errors.items?.[index]?.discountType}
                  helperText={touched.items?.[index]?.discountType && errors.items?.[index]?.discountType}
                >
                  <MenuItem value="">No discount</MenuItem>
                  {isAlleSKU ?
                    <MenuItem value="fixed">Dollar Off</MenuItem>
                    :
                    discountTypeOptions.map((option) => (
                      <MenuItem value={option.value} key={option.value}>
                        {option.label}
                      </MenuItem>
                    ))
                  }
                </TextField>
              }
            </FormControl>
          </Grid>
          <Grid item xs={12} lg={6} zeroMinWidth>
            <FormControl variant="outlined" fullWidth>
              {Object.keys(discountOptions).length > 0 ?
                <TextField
                  select
                  name={`items[${index}].discount`}
                  fullWidth
                  variant="outlined"
                  label="Select Discount"
                  value={discount}
                  onChange={handleOptionTypeChange}
                  InputLabelProps={{
                    style: {
                      ...labelStyle,
                      cursor: 'pointer'
                    },
                  }}
                  onBlur={handleBlur}
                  disabled={!discountType || !sku}
                  error={touched.items?.[index]?.discount && Boolean(errors.items?.[index]?.discount)}
                  helperText={touched.items?.[index]?.discount && errors.items?.[index]?.discount}
                >
                  {discountOptions?.[discountType]?.length ?
                    discountOptions[discountType].map((option) => {
                      const decimalScale = option.price_type === 'percent'
                        ? String(option.price).split('.')?.[1]?.length || 0
                        : 2;
                      return (
                        <MenuItem value={option.price} key={option.price}>
                          <TextFormattedNumber
                            value={Math.abs(option.price)}
                            suffix={discountType === 'percent' ? '%' : ''}
                            prefix={discountType === 'fixed' ? '$' : ''}
                            decimalScale={decimalScale}
                          />
                        </MenuItem>
                      );
                    })
                    :
                    <MenuItem value="">No Options</MenuItem>
                  }
                </TextField>
                :
                <NumberFormat
                  customInput={TextField}
                  variant="outlined"
                  name={`items[${index}].discount`}
                  label="Custom Discount"
                  InputLabelProps={{
                    style: {
                      ...labelStyle,
                      cursor: 'pointer'
                    },
                  }}
                  value={item.discount}
                  onValueChange={(values) => {
                    const { floatValue, value } = values;
                    handleOptionTypeChange({
                      target: { value: floatValue || value },
                    });
                  }}
                  onBlur={handleBlur}
                  disabled={!discountType || !sku}
                  error={touched.items?.[index]?.discount && Boolean(errors.items?.[index]?.discount)}
                  helperText={touched.items?.[index]?.discount && errors.items?.[index]?.discount}
                  suffix={discountType === 'percent' ? '%' : ''}
                  prefix={discountType === 'fixed' ? '$' : ''}
                  fixedDecimalScale
                  decimalScale={2}
                />
              }
            </FormControl>
          </Grid>
        </>
      }
    </Grid>
  );
};

export default QuoteItemSavings;
